/**
 * Tournament campaign-specific rules sections
 * Shows tournament rules, terms, and participation guidelines
 * Rendered by the shared RulesTab shell
 */

import { useMemo, type FC } from 'react';

import { IGameModes, TOURNAMENTS_CAMPAIGN_TYPE, useAutoJoin, type IParsedTournament } from 'features/campaigns';
import TransWithFallback from 'shared/components/Translations/TransWrapper';
import { formatCurrencyValue } from 'shared/utils/helpers';
import type { IRulesContentProps } from '../../../../../shared/tabs/RulesTab/RulesTab';
import RulesFormula from './FormulaRules';

import styles from './TournamentRulesContent.module.css';

export const TournamentRulesContent: FC<IRulesContentProps> = ({ campaign }) => {
  const tournament = campaign as IParsedTournament;
  const autoJoin = useAutoJoin(TOURNAMENTS_CAMPAIGN_TYPE);

  const minStakeInfo = useMemo(
    () => ({
      amount: tournament.minStake || tournament.minBet || 0,
      currency: tournament.baseCurrency || tournament.currency || '',
      hasRequirement: !!(tournament.minStake || tournament.minBet),
    }),
    [tournament],
  );

  const showBuyBonusText = tournament.restrictions?.eligibleGameModes?.includes(IGameModes.BUY_BONUS);

  return (
    <>
      <div className={styles.section}>
        <div className={styles.title}>
          <TransWithFallback i18nKey={'tournaments.rules.participation_rules'} />
        </div>
        <div className={styles.description}>
          {!autoJoin && (
            <span>
              <TransWithFallback
                i18nKey="tournaments.rules.join_tournament_text"
                components={{ span: <span className={styles.highlightedText} /> }}
              />
            </span>
          )}
          <TransWithFallback
            i18nKey="tournaments.rules.participating_game_text"
            components={{ span: <span className={styles.highlightedText} /> }}
            values={{ minSpinNumber: tournament.minSpinNumber || 1 }}
          />
          {showBuyBonusText && <TransWithFallback i18nKey="tournaments.rules.buy_bonus_text" />}
          {minStakeInfo.hasRequirement && (
            <span>
              <TransWithFallback
                i18nKey="tournaments.rules.min_participation_bet"
                components={{ span: <span className={styles.highlightedText} /> }}
                values={{
                  minStake: formatCurrencyValue(minStakeInfo.amount, 100),
                  currency: minStakeInfo.currency,
                }}
              />
            </span>
          )}
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.title}>
          <TransWithFallback i18nKey={'tournaments.rules.scoring_rules'} />
        </div>
        <div className={styles.description}>
          <RulesFormula
            formula={tournament.formula}
            currency={tournament.baseCurrency || ''}
            formulaParams={tournament.formulaParams}
          />
        </div>
      </div>
    </>
  );
};
